/**
 * Průvodce cestou — JEDNA definice tří otázek pro obě varianty (`/kviz` a
 * `/hledac-inspiraci`, vize C9 / F9.1).
 *
 * Průvodce NEZAKLÁDÁ vlastní odpovědi ani vlastní jmenný prostor adresy: každá odpověď
 * je hodnota kanonického kontraktu filtrů (`tripFilters.ts`) a její popisek je kanonický
 * český popisek. Proto:
 *
 * - odpověď kroku = klíč i hodnota filtru (`tripType`, `duration`, `bestMonth`), takže
 *   odkaz „zobrazit všechny" je TENTÝŽ stav přeložený `tripsHref` a vyhodnocení v API
 *   jde kanonickým filtrem zájezdů,
 * - adresu čte `parseFilterSearchParams` (neznámá hodnota se zahodí — pravidlo modulu)
 *   a píše `toSearchParams`; průvodce k nim přidává jen vlastní `krok`. Vnitřní názvy
 *   platí uvnitř; adresa je česky přes slovník (`webParams.ts`): `wizardUrl` překládá na
 *   výstupu, server čte už přeloženou adresu (`serverUrl`), prohlížeč `wizardStateFromAddress`,
 * - vlastní znění typu („Chci se válet") by shodilo `tripTypeNamingGuard.test.ts`, a to
 *   je správně: „válet se" JE `Pobytové u moře`.
 *
 * Varianta nese JEN to, čím se liší (cesta, nadpis, strop, druh výsledku). Kroky nenese —
 * kdo by je chtěl pro jednu variantu změnit, musí změnit typ, ne přepsat data.
 */
import { MONTH_LABELS_CZ, TRIP_TYPE_LABELS_CZ } from "./czLabels";
import { routes } from "./routes";
import { DURATION_BUCKETS, DURATION_BUCKET_BY_KEY, type DurationBucketKey } from "./tripBuckets";
import { emptyTripFilterState, parseFilterSearchParams, toSearchParams } from "./tripFilters";
import {
  TRIP_TYPES,
  type FulfillmentMode,
  type MediaResponse,
  type TripKind,
  type TripType,
} from "./types";
import { webHref, webParamsToInternal } from "./webParams";

/** Klíč kroku = klíč kontraktu filtrů. */
export type WizardStepKey = "tripType" | "duration" | "bestMonth";
export type WizardStepNumber = 1 | 2 | 3;

export interface WizardAnswerOption<V extends string | number = string | number> {
  value: V;
  label: string;
}

export interface WizardStep {
  key: WizardStepKey;
  question: string;
  answers: readonly WizardAnswerOption[];
}

const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] as const;

/** JEDINÉ místo s pořadím otázek. */
export const WIZARD_STEPS: readonly [WizardStep, WizardStep, WizardStep] = [
  {
    key: "tripType",
    question: "Jak chcete cestovat?",
    answers: TRIP_TYPES.map((value) => ({ value, label: TRIP_TYPE_LABELS_CZ[value] })),
  },
  {
    key: "duration",
    question: "Jak dlouho chcete cestovat?",
    answers: DURATION_BUCKETS.map((b) => ({ value: b.key, label: b.label })),
  },
  {
    key: "bestMonth",
    question: "Kdy chcete vyrazit?",
    answers: MONTHS.map((value) => ({ value, label: MONTH_LABELS_CZ[value - 1] })),
  },
];

export type WizardVariant = "kviz" | "hledac";
export const WIZARD_VARIANT_KEYS = ["kviz", "hledac"] as const satisfies readonly WizardVariant[];

/** Co průvodce vrací: zájezdy (kvíz), nebo země (hledač). Podle toho se větví API, admin i web. */
export type WizardResultKind = "trip" | "destination";

/**
 * Čím se varianty liší — a NIČÍM jiným. `heading` je jednořádkový nadpis stránky (P1:
 * „říká, co z toho vyjde"), `resultLimit` strop karet (F9.5), `resultKind` druh výsledku,
 * `offersNewsletter` nabídka odběru pod výsledkem (web) a její počty (admin).
 *
 * Kdo větví podle varianty, ptá se TADY (`WIZARD_VARIANTS[variant].resultKind`), ne na
 * jméno varianty.
 */
export interface WizardVariantConfig {
  path: string;
  heading: string;
  resultLimit: number;
  resultKind: WizardResultKind;
  offersNewsletter: boolean;
}

export const WIZARD_VARIANTS: Readonly<Record<WizardVariant, WizardVariantConfig>> = {
  kviz: {
    path: routes.kviz(),
    heading: "Najdeme vám až tři zájezdy",
    resultLimit: 3,
    resultKind: "trip",
    offersNewsletter: true,
  },
  hledac: {
    path: routes.hledacInspiraci(),
    heading: "Najdeme vám země, kam se vydat",
    resultLimit: 12,
    resultKind: "destination",
    offersNewsletter: false,
  },
};

/** Odpovědi průvodce; `null` = krok ještě nezodpovězený. */
export interface WizardAnswers {
  tripType: TripType | null;
  duration: DurationBucketKey | null;
  bestMonth: number | null;
}

/** Úplné odpovědi (tělo `POST /api/quiz`). */
export type CompleteWizardAnswers = { [K in keyof WizardAnswers]: NonNullable<WizardAnswers[K]> };

export interface WizardState {
  step: WizardStepNumber | "result";
  answers: WizardAnswers;
}

/** Parametr kroku v adrese — jediný klíč průvodce mimo jmenný prostor filtrů. */
export const WIZARD_STEP_PARAM = "krok";

const answerOf = (answers: WizardAnswers, key: WizardStepKey) => answers[key];

/** První krok bez odpovědi, nebo `null`, když jsou zodpovězené všechny. */
function firstUnanswered(answers: WizardAnswers): WizardStepNumber | null {
  const index = WIZARD_STEPS.findIndex((s) => answerOf(answers, s.key) === null);
  return index < 0 ? null : ((index + 1) as WizardStepNumber);
}

/**
 * Adresa → krok a odpovědi.
 *
 * `krok` platí, jen když je 1–3 a všechny předchozí kroky mají odpověď (jinak by šlo
 * adresou skočit na otázku měsíce bez typu). Bez `krok` a se všemi třemi odpověďmi je
 * to výsledek; ve všech ostatních případech první krok bez odpovědi.
 */
export function readWizardState(search: URLSearchParams): WizardState {
  const filters = parseFilterSearchParams(search);
  const answers: WizardAnswers = {
    tripType: filters.tripType,
    duration: (filters.duration[0] as DurationBucketKey | undefined) ?? null,
    bestMonth: filters.bestMonth,
  };
  const missing = firstUnanswered(answers);

  const rawStep = search.get(WIZARD_STEP_PARAM);
  if (rawStep !== null) {
    const n = Number(rawStep);
    const valid = n === 1 || n === 2 || n === 3;
    if (valid && (missing === null || n <= missing)) {
      return { step: n as WizardStepNumber, answers };
    }
  }
  return { step: missing ?? "result", answers };
}

/**
 * Webová adresa (prohlížeč, `window.location`) → krok a odpovědi. Jediné místo, kde se
 * adresa průvodce překládá dovnitř mimo server; `readWizardState` sám čte vnitřní názvy
 * (server ho volá nad `serverUrl`, která je přeložená). Dvojí překlad by odpovědi zahodil.
 */
export function wizardStateFromAddress(
  variant: WizardVariant,
  search: URLSearchParams,
): WizardState {
  return readWizardState(webParamsToInternal(WIZARD_VARIANTS[variant].path, search));
}

/**
 * Adresa kroku (`?…odpovědi&krok=N`) nebo výsledku (jen odpovědi). Dotaz skládá
 * kanonický `toSearchParams` — průvodce nemá vlastní zápis jmenného prostoru filtrů —
 * a na výstupu ho slovník přeloží na webové názvy a hodnoty.
 * Výsledková adresa nenese NIC jiného než tři odpovědi (výsledek se neukládá, F9.2).
 */
export function wizardUrl(
  variant: WizardVariant,
  answers: WizardAnswers,
  step?: WizardStepNumber,
): string {
  const params = toSearchParams({
    ...emptyTripFilterState(),
    tripType: answers.tripType,
    duration: answers.duration ? [answers.duration] : [],
    bestMonth: answers.bestMonth,
  });
  if (step !== undefined) params.set(WIZARD_STEP_PARAM, String(step));
  return webHref(WIZARD_VARIANTS[variant].path, params);
}

/** Úplné odpovědi, nebo `null`. */
export function completeAnswers(answers: WizardAnswers): CompleteWizardAnswers | null {
  const { tripType, duration, bestMonth } = answers;
  return tripType !== null && duration !== null && bestMonth !== null
    ? { tripType, duration, bestMonth }
    : null;
}

/** Normalizovaný klíč kombinace pro limit spuštění — varianta je jeho součástí. */
export function wizardComboKey(variant: WizardVariant, answers: CompleteWizardAnswers): string {
  return [variant, answers.tripType, answers.duration, answers.bestMonth].join("|");
}

/** Důvod shody jako KÓD (API) — větu z něj skládá `wizardReasonText`. */
export type WizardReason =
  | { dimension: "tripType"; value: TripType }
  | { dimension: "duration"; value: DurationBucketKey }
  | { dimension: "bestMonth"; value: number };

/** Česká věta důvodu z kanonických popisků — jedna pro web i testy. */
export function wizardReasonText(reason: WizardReason): string {
  switch (reason.dimension) {
    case "tripType":
      return TRIP_TYPE_LABELS_CZ[reason.value];
    case "duration":
      return DURATION_BUCKET_BY_KEY[reason.value]!.label;
    case "bestMonth":
      return `Nejlepší počasí: ${MONTH_LABELS_CZ[reason.value - 1].toLocaleLowerCase("cs")}`;
  }
}

/** Karta zájezdu ve výsledku kvízu. */
export interface WizardTripResult {
  kind: "trip";
  id: string;
  slug: string;
  title: string;
  tripType: TripType;
  tripKind: TripKind;
  fulfillmentMode: FulfillmentMode;
  priceFrom: number;
  priceIsFrom: boolean;
  durationDays: number | null;
  featuredImageMedia: MediaResponse | null;
  matchPercentage: number;
  reasons: WizardReason[];
}

/** Karta země ve výsledku hledače. */
export interface WizardDestinationResult {
  kind: "destination";
  id: string;
  slug: string;
  /** `DestinationPathRef.countrySlug` — u zemské úrovně vždy prázdné (plochá adresa). */
  countrySlug?: string | null;
  name: string;
  featuredImageMedia: MediaResponse | null;
  tripCount: number;
  matchPercentage: number;
  reasons: WizardReason[];
}

export type WizardResult = WizardTripResult | WizardDestinationResult;

/** Vyhodnocení odpovědí (API `evaluateTripWizard`). */
export interface WizardEvaluation {
  /** Velikost celé množiny (kvíz: zájezdy tvrdé množiny, hledač: země s nenulovým roll-upem). */
  total: number;
  results: WizardResult[];
}

/** `data` odpovědi `POST /api/quiz` — JEDEN kontrakt pro API i web. */
export interface WizardResponseData extends WizardEvaluation {
  quizId: string;
  variant: WizardVariant;
}
